import { useEffect } from 'react';

import { GetDeviceListFromLineage } from '@/api/fe-api/view/queries/get-device-list-from-lineage';

import type { BatteryLevel } from '../entities/battery-level';
import type { AllIdFilters } from '../entities/filters';

export type UseLevelFilterDeviceProps = {
  id: string | undefined;
  filterKey: string;
  level: BatteryLevel;
  currentValue: string | string[] | undefined;
  disabled: boolean;
  setSelectedValue: (value: string[]) => void;
  onChange: (value: Partial<AllIdFilters>) => void;
};

/**
 * Auto-populates filter selections based on device lineage.
 *
 * This hook is designed for battery hierarchy levels where a device selection
 * should automatically populate all sub-components at a given level.
 *
 * NOTE: This should NOT be used for PCS domain, as PCS units should be
 * manually selected by users without automatic population based on device lineage.
 */
export function useLevelFilterDevice({
  id,
  filterKey,
  level,
  currentValue,
  disabled,
  setSelectedValue,
  onChange,
}: UseLevelFilterDeviceProps) {
  const { data: deviceList } = GetDeviceListFromLineage.useQuery({
    id,
    level,
  });

  useEffect(() => {
    if (!id || disabled) {
      return;
    }

    if (!deviceList || deviceList.length === 0) {
      return;
    }

    const normalizedCurrentValue = Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : [];

    if (normalizedCurrentValue.length > 0) {
      return;
    }

    const lineageIds = deviceList.map(({ id }) => id).filter(Boolean);

    if (lineageIds.length === 0) {
      return;
    }

    setSelectedValue(lineageIds);
    onChange({
      [filterKey]: lineageIds,
    });
  }, [
    currentValue,
    deviceList,
    disabled,
    filterKey,
    id,
    onChange,
    setSelectedValue,
  ]);
}
